// src/data/lifeInsurance.js
import { apolloClient } from "@/apollo";
import { gql } from "@apollo/client/core";

/**
 * GraphQL Query for fetching life insurance products
 */
const GET_LIFE_PRODUCTS = gql`
  query GetLifeProducts {
    products_life_product(order_by: { id: asc }) {
      id
      slug
      Type_product
      Name_product
      top_badge
      title_primary
      title_secondary
      download_brochure
      conditions_heading
      benefits_heading
      content_title
      cardCoverImage
      bannerImage
      PageBanner
      rankProduct

      features(order_by: { id: asc }) {
        id
        title
        description
        icon
      }

      conditions(order_by: { id: asc }) {
        id
        title
        description
        icon
        conditions_Icon {
          id
          url
          name
        }
      }

      benefitSections(order_by: { id: asc }) {
        id
        title
        intro
        items
      }
    }
  }
`;

/**
 * Fetch life insurance products from Hasura Database via Apollo Client
 * @returns {Promise<Array>} Array of life insurance products
 */
async function fetchProductsFromDatabase() {
  try {
    const { data } = await apolloClient.query({
      query: GET_LIFE_PRODUCTS,
      fetchPolicy: "network-only",
    });

    if (!data || !data.products_life_product) {
      console.warn("No life products found in database");
      return [];
    }

    // Transform data to match original structure
    return data.products_life_product.map((product) => ({
      id: product.id,
      slug: product.slug,
      Type_product: product.Type_product,
      Name_product: product.Name_product,
      top_badge: product.top_badge,
      title_primary: product.title_primary,
      title_secondary: product.title_secondary,
      download_brochure: product.download_brochure,
      conditions_heading: product.conditions_heading,
      benefits_heading: product.benefits_heading,
      content_title: product.content_title,
      rankProduct: product.rankProduct,

      // Map cardCoverImage to image
      image: product.cardCoverImage,

      // Features array
      features: product.features || [],

      // Conditions array
      conditions: (product.conditions || []).map((condition) => ({
        id: condition.id,
        title: condition.title,
        description: condition.description,
        icon: condition.icon,
        icon2: condition.conditions_Icon?.url || null, // ✅ เปลี่ยนจาก icon2 เป็น conditions_Icon
      })),
      // Benefit sections array
      benefitSections: product.benefitSections || [],

      // Parse banner images (if they're JSON strings)
      // ✅ bannerImage - Return array ทั้งหมด
      bannerImage: (() => {
        if (typeof product.bannerImage === "string") {
          return JSON.parse(product.bannerImage);
        }
        return product.bannerImage;
      })(),

      // ✅ PageBanner - Return array ทั้งหมด
      PageBanner: (() => {
        if (typeof product.PageBanner === "string") {
          return JSON.parse(product.PageBanner);
        }
        return product.PageBanner;
      })(),
    }));
  } catch (error) {
    console.error("Error fetching life insurance products:", error);
    // Return empty array on error (ไม่ให้ระบบ crash)
    return [];
  }
}

/**
 * Export lifeInsuranceProducts (Compatible กับโค้ดเดิม)
 * นี่คือ array ที่ไฟล์อื่นๆ import ไปใช้
 *
 * หมายเหตุ: เนื่องจากข้อมูลมาจาก Apollo Client จะเป็น empty array ตอนแรก
 * ต้องรอให้ loadLifeInsuranceProducts() ทำงานเสร็จก่อน
 */
export const lifeInsuranceProducts = [];

/**
 * Loading state tracker
 */
let isLoading = false;
let isLoaded = false;

/**
 * Load life insurance products and populate the array
 * @returns {Promise<Array>} The populated array
 */
export async function loadLifeInsuranceProducts() {
  // ถ้ากำลังโหลดอยู่หรือโหลดเสร็จแล้ว ให้ return array ที่มีอยู่
  if (isLoading) {
    // รอให้การโหลดปัจจุบันเสร็จก่อน
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!isLoading) {
          clearInterval(checkInterval);
          resolve(lifeInsuranceProducts);
        }
      }, 100);
    });
  }

  // ถ้าโหลดไปแล้วและมีข้อมูล ให้ return ทันที
  if (isLoaded && lifeInsuranceProducts.length > 0) {
    return lifeInsuranceProducts;
  }

  isLoading = true;

  try {
    const products = await fetchProductsFromDatabase();

    // Clear และเพิ่มข้อมูลใหม่ลงใน array (maintain reference)
    lifeInsuranceProducts.splice(0, lifeInsuranceProducts.length);
    lifeInsuranceProducts.push(...products);

    isLoaded = true;
    return lifeInsuranceProducts;
  } catch (error) {
    console.error("Failed to load life insurance products:", error);
    return lifeInsuranceProducts;
  } finally {
    isLoading = false;
  }
}

/**
 * Force reload products (bypass cache)
 * @returns {Promise<Array>} The refreshed array
 */
export async function reloadLifeInsuranceProducts() {
  isLoaded = false;
  return loadLifeInsuranceProducts();
}

/**
 * Get a single product by ID
 * @param {string} productId - Product ID to find
 * @returns {Object|null} Product object or null if not found
 */
export function getLifeProductById(productId) {
  return (
    lifeInsuranceProducts.find((product) => product.id === productId) || null
  );
}

/**
 * Get a single product by slug
 * @param {string} slug - Product slug to find
 * @returns {Object|null} Product object or null if not found
 */
export function getLifeProductBySlug(slug) {
  return lifeInsuranceProducts.find((product) => product.slug === slug) || null;
}

// Auto-load when module is imported
// loadLifeInsuranceProducts(); // Disabled: Let components load manually
